/**
 * Owns a single promo tab's KPI (visible-column) customizations: the metric/spend
 * keys the user has explicitly hidden or added, split by view.
 *
 * State is bundled here so `PromoTab` (which survives the first-save spinner
 * remount of `PromoMetricsSection`) can own it and pass one prop down. Because it
 * lives with `PromoTab`, the selection is unique per open promo tab, survives
 * save/update, and is deliberately dropped when the tab is closed (the tab, and
 * hence this state, unmounts) so a reopened promo starts from defaults.
 */
import { type Dispatch, type SetStateAction, useState } from 'react';

export interface PromoKpiColumns {
    hiddenMetricKeys: Set<string>;
    extraMetricKeys: Set<string>;
    hiddenSpendKeys: Set<string>;
    extraSpendKeys: Set<string>;
    setHiddenMetricKeys: Dispatch<SetStateAction<Set<string>>>;
    setExtraMetricKeys: Dispatch<SetStateAction<Set<string>>>;
    setHiddenSpendKeys: Dispatch<SetStateAction<Set<string>>>;
    setExtraSpendKeys: Dispatch<SetStateAction<Set<string>>>;
}

export function usePromoKpiColumns(): PromoKpiColumns {
    const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => new Set());
    const [extraMetricKeys, setExtraMetricKeys] = useState<Set<string>>(() => new Set());
    const [hiddenSpendKeys, setHiddenSpendKeys] = useState<Set<string>>(() => new Set());
    const [extraSpendKeys, setExtraSpendKeys] = useState<Set<string>>(() => new Set());

    return {
        hiddenMetricKeys,
        extraMetricKeys,
        hiddenSpendKeys,
        extraSpendKeys,
        setHiddenMetricKeys,
        setExtraMetricKeys,
        setHiddenSpendKeys,
        setExtraSpendKeys,
    };
}
